import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Banknote, FileCheck2, Store, ChevronLeft, ChevronRight, PlayCircle, Award, Users, Building2, ShieldCheck, ArrowRight } from "lucide-react";
import RotatingQuote from "../components/RotatingQuote";
import SearchBar from "../components/SearchBar";
import PropertyCard from "../components/PropertyCard";
import WealthCalculator from "../components/WealthCalculator";
import { Properties, formatINR } from "../lib/api";
import { HERO_NOUN } from "../data/quotes";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const FAQS = [
  { q: "What is Zelo?", a: "Zelo is a platform that offers verified, rent-generating pre-leased commercial properties tenanted by India's most credible brands." },
  { q: "What is a Pre-Leased Property?", a: "A property already rented out to a tenant — meaning rental income starts from Day 1 of ownership transfer." },
  { q: "Who are the typical tenants in Zelo properties?", a: "Top national and international brands — Banks, Jewelers, QSRs, Retail Chains, Corporate offices and more." },
  { q: "How soon can I start earning rental income?", a: "From the very first day of ownership transfer. Every Zelo asset comes with a signed lease and existing rent." },
  { q: "Will I get a sale deed in my name?", a: "Yes. You receive full legal ownership registered in your name — exactly like any other property purchase." },
  { q: "Is documentation and due diligence already done?", a: "Yes. Every property is legally verified — lease agreements, approvals, and tenant checks are all in place." },
  { q: "Can NRIs invest in Zelo properties?", a: "Absolutely. NRIs invest seamlessly and receive rent directly into their Indian bank accounts." },
  { q: "Are these properties bank-financeable?", a: "Yes. All pre-leased properties on Zelo are eligible for commercial property loans (LRD)." },
];

const TENANT_LOGOS = [
  "https://logo.clearbit.com/mcdonalds.com",
  "https://logo.clearbit.com/mywestside.com",
  "https://logo.clearbit.com/joyalukkas.in",
  "https://logo.clearbit.com/hdfcbank.com",
  "https://logo.clearbit.com/icicibank.com",
  "https://logo.clearbit.com/starbucks.com",
  "https://logo.clearbit.com/tanishq.co.in",
  "https://logo.clearbit.com/croma.com",
  "https://logo.clearbit.com/dominos.co.in",
];

export default function PreLeasedHome() {
  const [topPicks, setTopPicks] = useState([]);
  const [deal, setDeal] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    Properties.topPicks().then((d) => setTopPicks(d.items || [])).catch(() => {});
    Properties.dealOfMonth().then((d) => setDeal(d.item)).catch(() => {});
  }, []);

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * 380, behavior: "smooth" });
  };

  return (
    <div data-testid="pre-leased-home">
      {/* Beta banner */}
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-center py-2.5 text-sm font-medium" data-testid="pre-leased-beta-banner">
        <span className="inline-flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-wider">Beta</span>
          You're previewing our Pre-Leased Properties experience — currently under active development.
          <Link to="/retail-leasing" className="underline font-semibold hover:no-underline">Switch to Retail Leasing →</Link>
        </span>
      </div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-50/60 via-white to-white" />
        <div className="absolute -top-20 right-0 w-[600px] h-[600px] rounded-full bg-brand/10 blur-3xl -z-10" />

        <div className="container-zelo pt-16 md:pt-24 pb-20 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-neutral-200 text-xs font-semibold tracking-[0.2em] uppercase text-brand">
                <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                Pre-Leased · Brand Tenanted · Rent-Ready
              </div>
              <div className="mt-6">
                <RotatingQuote />
              </div>
              <p className="mt-6 italic text-lg md:text-xl text-neutral-500 max-w-xl text-balance" data-testid="hero-noun">
                {HERO_NOUN}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/properties" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="hero-explore-cta">
                  Explore Properties <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/calculator" className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-neutral-700 hover:text-brand transition" data-testid="hero-calculator-cta">
                  Try the Wealth Calculator <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Deal of the month */}
            <div className="lg:col-span-5">
              {deal && (
                <Link to={`/properties/${deal.id}`} className="block group" data-testid="deal-of-the-month">
                  <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-white hover:-translate-y-1 transition-all duration-500 shadow-soft">
                    <div className="px-5 py-3 flex items-center justify-between bg-gradient-to-r from-brand to-brand-600 text-white">
                      <span className="text-xs font-bold tracking-[0.18em] uppercase">⭐ Deal of the Month</span>
                      <span className="text-xs opacity-80">View →</span>
                    </div>
                    <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
                      <img src={deal.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <div className="text-sm text-neutral-500">{deal.tenant_industry} @ {deal.city}</div>
                      <div className="font-heading text-xl font-medium mt-1">{deal.code || `ZPL${(deal.id || "").slice(0, 4).toUpperCase()}`}</div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                        <Mini l="Monthly Rent" v={formatINR(deal.monthly_rent)} />
                        <Mini l="Yield" v={`${deal.roi}%`} />
                        <Mini l="Lease" v={`${deal.lease_period_years} yr`} />
                      </div>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>

          <div className="mt-14 max-w-3xl ml-auto">
            <SearchBar />
          </div>
        </div>
      </section>

      {/* Trust strip (anonymized — categories only) */}
      <section className="py-10 border-y border-neutral-200 bg-white" data-testid="tenants-strip">
        <div className="container-zelo">
          <div className="text-center label-tiny mb-6">Brand-tenanted assets · Verified & Anonymised</div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {["National Banks", "International QSR Brands", "Premium Jewellery Chains", "Listed Retail Anchors", "Specialty F&B", "Electronics Hyper-Markets", "Pharmacy Anchors", "Fashion Houses"].map((c, i) => (
              <span key={i} className="px-4 py-2 rounded-full bg-brand-50 text-brand text-sm font-medium border border-brand/10">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* TOP PICKS */}
      <section className="py-20 md:py-28">
        <div className="container-zelo">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="label-tiny text-brand">Curated · ROI-Sorted</div>
              <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3">Top Picks by Zelo</h2>
              <p className="mt-3 text-neutral-500 max-w-xl">High-conviction, pre-leased assets generating rent from Day 1. Hand-picked for capital safety and locked-in cashflow.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => scroll(-1)} aria-label="Scroll left" className="w-10 h-10 rounded-full border border-neutral-200 hover:border-brand hover:text-brand transition flex items-center justify-center" data-testid="top-picks-scroll-left"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => scroll(1)} aria-label="Scroll right" className="w-10 h-10 rounded-full border border-neutral-200 hover:border-brand hover:text-brand transition flex items-center justify-center" data-testid="top-picks-scroll-right"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>

          <div ref={scrollerRef} className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4" data-testid="top-picks-scroller">
            {topPicks.map((p) => (
              <div key={p.id} className="min-w-[85%] sm:min-w-[60%] lg:min-w-[31%] snap-start">
                <PropertyCard p={p} testIdPrefix="top-pick" />
              </div>
            ))}
          </div>

          <div className="mt-8">
            <Link to="/properties" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="top-picks-explore-all">
              Explore All Properties <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY SMART INVESTORS */}
      <section className="py-20 md:py-28 bg-neutral-50/60 relative overflow-hidden" data-testid="why-smart-investors">
        <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
        <div className="container-zelo relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
              <div className="label-tiny text-brand">The Income-First Model</div>
              <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3 text-balance">Properties That Work as Hard as You Do</h2>
              <p className="mt-5 text-neutral-500 leading-relaxed text-pretty">
                Zelo doesn't list assets. We list outcomes. Every property comes with a tenant, a signed lease, and rent flowing into your account from Day 1 — institutional-grade income, individual access.
              </p>
              <Link to="/properties" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="why-explore-cta">
                Explore Properties <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Pillar Icon={BadgeCheck} title="Leased to Top Brands" desc="National banks, international QSR chains, premium jewellery anchors — credible categories paying you rent." />
              <Pillar Icon={Banknote} title="Rent from Day One" desc="No waiting for tenants. No vacancy risk. Rent hits your account from the first day of ownership transfer." />
              <Pillar Icon={FileCheck2} title="Fully Verified Documents" desc="Lease deeds, tenant KYC, escalation clauses, lock-ins — every paper is verified, signed, and legally bulletproof." />
              <Pillar Icon={ShieldCheck} title="Banks Prefer Financing" desc="Pre-leased commercial assets are bank-favoured. LRD-eligible up to 70% with leading lenders." />
            </div>
          </div>
        </div>
      </section>

      {/* AWARD / TRUST */}
      <section className="py-20 md:py-28" data-testid="trust-award">
        <div className="container-zelo">
          <div className="text-center mb-12">
            <div className="label-tiny text-brand">India's Most Trusted Pre-Leased Platform</div>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3 text-balance">This Is What Peace of Mind Looks Like</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-neutral-900 aspect-[16/10] relative group cursor-pointer">
              <img src="https://images.unsplash.com/photo-1767974785671-cfc4c8727147?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="Zelo Award" className="w-full h-full object-cover opacity-60 group-hover:opacity-70 transition" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-8">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-4 group-hover:scale-110 transition"><PlayCircle className="w-9 h-9" /></div>
                <div className="font-heading text-2xl md:text-3xl font-medium">Awarded by IIM-A Director,<br/>Mr. Praful Dholakia</div>
                <div className="text-sm opacity-80 mt-2">Excellence in Commercial Real Estate</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Trust Icon={BadgeCheck} title="Verified, Leased Assets Only" desc="Every property already pays rent" />
              <Trust Icon={Users} title="Trusted by Investors Across India" desc="HNIs, NRIs, family offices already earning with Zelo" />
              <Trust Icon={FileCheck2} title="Legal & Financial Due Diligence" desc="Every deal comes clean, clear, and compliant" />
              <Trust Icon={Banknote} title="Bank-Backed Confidence" desc="Assets eligible for easy commercial financing" />
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-20 md:py-28 bg-neutral-50/60" data-testid="home-calculator">
        <div className="container-zelo">
          <div className="text-center mb-12">
            <div className="label-tiny text-brand">Calculator · LRD · Mortgage</div>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3 text-balance">Run the math. See the cashflow.</h2>
            <p className="mt-3 text-neutral-500 max-w-xl mx-auto">Adjust your numbers — see EMI, net cashflow, and rental projections instantly.</p>
          </div>
          <WealthCalculator />
          <div className="text-center mt-10">
            <Link to="/properties" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="calc-explore-cta">
              Explore Properties <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="py-20 md:py-28" data-testid="journey-section">
        <div className="container-zelo">
          <div className="text-center mb-14">
            <div className="label-tiny text-brand">3 Simple Steps</div>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3">Your Journey with Zelo</h2>
            <p className="mt-3 text-neutral-500 max-w-2xl mx-auto">From individual investors to global institutions, Zelo delivers a smooth, secure, rent-first experience.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Explore Verified Properties", d: "Browse only pre-leased, brand-tenanted, income-producing assets across India's top cities." },
              { n: "02", t: "Review Lease Details & Docs", d: "Get full transparency — lease term, lock-ins, escalations, tenant KYC, ROI math." },
              { n: "03", t: "Invest & Start Earning Rent", d: "Sale deed in your name. Rent flowing into your account. From Day 1. No middlemen." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-neutral-200 bg-white p-8 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-baseline gap-3">
                  <span className="font-heading text-5xl font-medium text-brand-100">{s.n}</span>
                </div>
                <div className="font-heading text-2xl font-medium tracking-tight mt-3">{s.t}</div>
                <div className="text-neutral-500 mt-3 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-neutral-50/60" data-testid="faq-section">
        <div className="container-zelo grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-tiny text-brand">FAQs</div>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3 text-balance">Know everything about Zelo.</h2>
            <p className="mt-4 text-neutral-500">The process, the documents, and how to start earning rent — answered.</p>
            <div className="mt-8 p-6 rounded-2xl border border-neutral-200 bg-white">
              <div className="text-sm font-semibold text-neutral-900">Can't find an answer?</div>
              <div className="text-sm text-neutral-500 mt-1">Reach Aman Nainani — Chief Sales Officer.</div>
              <div className="mt-3 space-y-1 text-sm">
                <div><a href="mailto:aman@zelolease.com" className="text-brand">aman@zelolease.com</a></div>
                <div><a href="tel:+918758115155" className="text-brand">+91 87581 15155</a></div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="rounded-2xl border border-neutral-200 bg-white px-5">
                  <AccordionTrigger className="text-left font-heading text-base md:text-lg font-medium" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-neutral-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}

function Pillar({ Icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-7 hover:-translate-y-1 transition-all duration-300">
      <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div className="font-heading text-xl font-medium tracking-tight mt-5">{title}</div>
      <div className="text-neutral-500 mt-2 leading-relaxed">{desc}</div>
    </div>
  );
}

function Trust({ Icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 hover:-translate-y-1 transition-all duration-300">
      <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div className="font-heading text-lg font-medium mt-4">{title}</div>
      <div className="text-sm text-neutral-500 mt-1">{desc}</div>
    </div>
  );
}

function Mini({ l, v }) {
  return (
    <div>
      <div className="label-tiny">{l}</div>
      <div className="font-medium text-neutral-900 text-sm mt-0.5">{v}</div>
    </div>
  );
}
